import { ChevronDown, ChevronRight } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  selectRegions,
  selectRegionsLoading,
  selectTenant,
  useAppSelector,
} from '@/store'
import ScrollButton, { ScrollPosition } from './ScrollButton'
import TreeNode from './TreeNode'
import {
  BasicBranchSelectProps,
  findBranch,
  findBranchRegion,
  findRegion,
  useBranchSelection,
} from './branchSelectUtil'

// ----------------------------------------------------------------------
// MAIN COMPONENT
// ----------------------------------------------------------------------

export const BasicBranchSelect = ({
  onChange,
  value,
  branchType,
  resetBranchSelect,
}: BasicBranchSelectProps) => {
  const { id } = useParams()
  const tenant = useAppSelector(selectTenant)
  const REGIONS = useAppSelector(selectRegions)
  const regionsLoading = useAppSelector(selectRegionsLoading)

  const selectableRegions = useMemo(() => {
    if (REGIONS.length === 0) return []
    if (id === 'spend') return REGIONS
    return REGIONS.filter((region) => region.name !== 'Other')
  }, [id, REGIONS])

  const {
    selection,
    expandedRegions,
    handlePredefinedSelect,
    handleRegionSelect,
    handleBranchSelect,
    toggleRegionExpansion,
  } = useBranchSelection({
    regions: REGIONS,
    selectableRegions,
    value,
    branchType,
    tenant,
    onChange,
    resetBranchSelect,
  })

  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({
    isAtTop: true,
    isAtBottom: false,
  })

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const isAtTop = scrollTop === 0
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1
    setScrollPosition({ isAtTop, isAtBottom })
  }, [])

  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      setScrollPosition({ isAtTop: true, isAtBottom: false })
      const container = document.querySelector('.picker-content')
      if (container) container.scrollTop = 0
    }
  }, [])

  const getSelectionText = useCallback(() => {
    const { predefinedSelection, selectedRegions, selectedBranches } = selection

    if (predefinedSelection === 'all') return 'All Stores'
    if (predefinedSelection === 'same') return 'Same Stores'
    if (predefinedSelection === 'new') return 'New Stores'

    const totalSelected = selectedRegions.length + selectedBranches.length
    if (totalSelected === 0) return 'Select branches'

    const parts: string[] = []

    // Add regions
    selectedRegions.forEach((rId) => {
      const r = findRegion(rId, REGIONS)
      if (r) parts.push(r.name)
    })

    // Add standalone branches
    selectedBranches.forEach((bId) => {
      const b = findBranch(bId, REGIONS)
      const parentRegion = findBranchRegion(bId, REGIONS)
      // Only add if parent region is NOT selected
      if (b && parentRegion && !selectedRegions.includes(parentRegion.id)) {
        parts.push(b.name)
      }
    })

    return parts.join(', ')
  }, [selection, REGIONS])

  const getAllSelectedItems = useCallback(() => {
    const { selectedRegions, selectedBranches } = selection
    const items: string[] = []

    selectedRegions.forEach((rId) => {
      const r = findRegion(rId, REGIONS)
      if (r) {
        items.push(r.name)
        r.branches.forEach((b) => items.push(`  └─ ${b.name}`))
      }
    })

    selectedBranches.forEach((bId) => {
      const b = findBranch(bId, REGIONS)
      const parentRegion = findBranchRegion(bId, REGIONS)
      if (b && parentRegion && !selectedRegions.includes(parentRegion.id)) {
        items.push(b.name)
      }
    })

    return items.join('\n')
  }, [selection, REGIONS])

  // Loading State
  if (regionsLoading || REGIONS.length === 0) {
    return (
      <Button
        variant="outline"
        className="justify-start h-8 font-normal text-black w-[300px]"
        disabled
      >
        <span className="truncate">Loading...</span>
        <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
      </Button>
    )
  }

  const currentPredefined = selection.predefinedSelection

  return (
    <Popover onOpenChange={handleOpenChange}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="justify-start h-8 font-normal w-[300px]"
              >
                <span className="truncate">{getSelectionText()}</span>
                <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="max-w-[300px] whitespace-pre-line"
          >
            {getAllSelectedItems()}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent className="w-[400px] p-0">
        <div className="h-[350px] flex flex-col">
          {!scrollPosition.isAtTop && (
            <ScrollButton
              direction="up"
              onClick={() => {
                const container = document.querySelector('.picker-content')
                if (container) container.scrollTop -= 100
              }}
            />
          )}

          <div
            className="flex-1 p-2 overflow-y-auto picker-content [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            onScroll={handleScroll}
          >
            {/* Predefined Options */}
            <>
              <div className="ml-7 space-y-1 h-8 p-0">
                <TreeNode
                  icon={null}
                  label="All Stores"
                  checked={currentPredefined === 'all'}
                  onCheck={(c) => handlePredefinedSelect('all', c)}
                  onClick={() => {}}
                  className="text-sm mb-1"
                  type="date"
                />
              </div>
              {tenant === 'dev' && (
                <>
                  <div className="ml-7 space-y-1 h-8 p-0">
                    <TreeNode
                      icon={null}
                      label="Same Stores"
                      checked={currentPredefined === 'same'}
                      onCheck={(c) => handlePredefinedSelect('same', c)}
                      onClick={() => {}}
                      className="text-sm mb-1"
                      type="date"
                    />
                  </div>
                  <div className="ml-7 space-y-1 h-8 p-0">
                    <TreeNode
                      icon={null}
                      label="New Stores"
                      checked={currentPredefined === 'new'}
                      onCheck={(c) => handlePredefinedSelect('new', c)}
                      onClick={() => {}}
                      className="text-sm mb-1"
                      type="date"
                    />
                  </div>
                </>
              )}
            </>

            {/* Tree View */}
            {selectableRegions.map((region) => {
              const isExpanded = expandedRegions.has(region.id)
              const isSelected = selection.selectedRegions.includes(region.id)

              return (
                <div key={region.id} className="mb-1">
                  <TreeNode
                    icon={
                      isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )
                    }
                    label={region.name}
                    checked={isSelected}
                    onCheck={(c) => handleRegionSelect(region.id, c)}
                    onClick={() => toggleRegionExpansion(region.id)}
                    className="text-sm"
                    type="date"
                  />

                  {isExpanded && (
                    <div className="ml-10 mt-1">
                      {region.branches.map((branch) => (
                        <TreeNode
                          key={branch.id}
                          icon={null}
                          label={branch.name}
                          checked={selection.selectedBranches.includes(
                            branch.id
                          )}
                          onCheck={(c) =>
                            handleBranchSelect(region.id, branch.id, c)
                          }
                          onClick={() => {}}
                          className="text-sm"
                          type="date"
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {!scrollPosition.isAtBottom && (
            <ScrollButton
              direction="down"
              onClick={() => {
                const container = document.querySelector('.picker-content')
                if (container) container.scrollTop += 100
              }}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
